// BHP-1046: uniform application error. Backend2.0 returns RFC 7807
// `application/problem+json`; this maps it to a single shape the UI can rely on,
// with no assumptions about the legacy `ApiResponse.meta.error` envelope.
// BHP-1070: validation responses (API Platform 422) also carry a `violations`
// list, surfaced here so forms can show field-level errors.
// BHP-1048: turning that list into per-input messages lives in
// `errorMessage.ts::toFormErrors`, not here. It needs to know which fields the
// form actually renders — a violation naming a field that is not on screen has
// to fall back to a banner instead of disappearing, and a plain field→message
// map cannot express that.

export interface FieldViolation {
  propertyPath: string;
  message: string;
}

/**
 * BHP-1144: what went wrong, apart from the status code.
 * - `response`: the server answered with an error status;
 * - `transport`: fetch rejected — the request never got an answer (status 0);
 * - `invalid-body`: a success status whose body is not what was asked for, such
 *   as a WAF or proxy page in place of a PDF.
 */
export type ApiErrorKind = 'response' | 'transport' | 'invalid-body';

export interface ProblemDetails {
  title: string;
  detail: string;
  status: number;
  violations?: FieldViolation[];
  /**
   * BHP-1144: the per-request reference id the client generated and sent as
   * `X-Client-Request-Id`. The backend tags its logs and Sentry with the same
   * value, so a customer who quotes the id lets support find the failure.
   * Empty when the error was built without one (legacy paths, tests).
   */
  requestId?: string;
  /** Defaults to `transport` for status 0, otherwise `response`. */
  kind?: ApiErrorKind;
  /**
   * BHP-1144: Backend2.0 itself answered (a problem+json body), so its logs
   * and Sentry carry `requestId`. False for transport failures, invalid
   * bodies and error pages from a proxy or ingress in front of it. Defaults to
   * true for a `response`, which is what errors built in code represent.
   */
  fromBackend?: boolean;
  /** The underlying error, e.g. the TypeError a rejected fetch threw. */
  cause?: unknown;
}

export class ApiError extends Error {
  readonly title: string;
  readonly detail: string;
  readonly status: number;
  readonly violations: FieldViolation[];
  readonly requestId: string;
  readonly kind: ApiErrorKind;
  readonly fromBackend: boolean;
  // Declared here because the ES2021 lib has no Error.cause yet.
  readonly cause: unknown;

  constructor({
    title,
    detail,
    status,
    violations = [],
    requestId = '',
    kind = status === 0 ? 'transport' : 'response',
    fromBackend = kind === 'response',
    cause
  }: ProblemDetails) {
    super(`${status} ${title}${detail !== '' ? `: ${detail}` : ''}`);
    this.name = 'ApiError';
    this.title = title;
    this.detail = detail;
    this.status = status;
    this.violations = violations;
    this.requestId = requestId;
    this.kind = kind;
    this.fromBackend = fromBackend;
    this.cause = cause;
  }

  /**
   * BHP-1144: a 5xx, a transport failure or an invalid body — the cases whose
   * text is technical or third-party and must never reach a user. A 4xx
   * (validation, auth, not-found) carries a backend reason meant for the user
   * and is shown as-is.
   */
  isUnexpected(): boolean {
    return this.kind !== 'response' || this.status === 0 || this.status >= 500;
  }
}
